import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const VictorianADUDesignMilpitas = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Victorian ADU Design & Construction in Milpitas",
    "description": "Custom Victorian ADUs in Milpitas. Expert design & construction by UDA ADU. Elegant Victorian architecture with modern amenities.",
    "provider": {
      "@type": "Organization",
      "name": "UDA ADU"
    },
    "url": "https://www.udaadu.com/victorian-adu-design-milpitas"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/victorian-adu-design-milpitas" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <Navigation />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] relative">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
            <iframe
              src="https://calendly.com/udaadu/30min"
              width="100%"
              height="100%"
              frameBorder="0"
              className="rounded-lg"
            ></iframe>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Victorian ADU Design & Construction in Milpitas
          </h1>
          <p className="text-xl text-gray-200 max-w-4xl mx-auto mb-8">
            Bring timeless elegance to your Milpitas property with a stunning Victorian ADU. Our expert designs combine the ornate beauty and architectural sophistication of the Victorian era with modern functionality and comfort.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Schedule Design Consultation
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/landing">Try UDA AI Designer</Link>
            </Button>
          </div>
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-white">
              Authentic Victorian ADU Features
            </h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Ornate Gingerbread Trim</h3>
                  <p className="text-gray-200">Intricate decorative woodwork that defines Victorian elegance</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Bay Windows</h3>
                  <p className="text-gray-200">Classic protruding windows that add charm and natural light</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Steep Gabled Roofs</h3>
                  <p className="text-gray-200">Dramatic rooflines with decorative bargeboards and finials</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Wraparound Porches</h3>
                  <p className="text-gray-200">Welcoming covered porches with ornate columns and railings</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Rich Color Schemes</h3>
                  <p className="text-gray-200">Bold, period-appropriate color palettes with accent details</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-white">
              Victorian ADU Benefits in Milpitas
            </h2>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-white mb-2">Distinctive Character</h3>
                <p className="text-gray-200">Create a unique, memorable property that stands out in Milpitas</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-white mb-2">Premium Value</h3>
                <p className="text-gray-200">Victorian architecture commands higher property values and rental rates</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-white mb-2">Timeless Appeal</h3>
                <p className="text-gray-200">Classic Victorian design never goes out of style</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-white mb-2">Functional Elegance</h3>
                <p className="text-gray-200">High ceilings and large windows create spacious, airy interiors</p>
              </div>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-center mb-12 text-white">
            Victorian ADU Design Process in Milpitas
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Heritage Consultation</h3>
              <p className="text-gray-200">Explore authentic Victorian design elements for your property</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Period-Accurate Design</h3>
              <p className="text-gray-200">Create authentic Victorian plans with modern conveniences</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Milpitas Permits</h3>
              <p className="text-gray-200">Navigate local building codes and design review process</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">4</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Artisan Construction</h3>
              <p className="text-gray-200">Skilled craftsmanship bringing Victorian elegance to life</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-muted rounded-lg p-8 text-center">
          <h2 className="text-3xl font-semibold mb-4 text-white">
            Create Your Victorian Masterpiece in Milpitas
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
            Design an exquisite Victorian ADU that captures the romance and elegance of a bygone era while providing all the comforts of modern living. Our Milpitas specialists are ready to bring your Victorian dream to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Start Your Victorian Project
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/adu-calculator">Get Victorian Quote</Link>
            </Button>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold mb-4 text-white">Explore More Milpitas ADU Styles</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/mediterranean-adu-design-milpitas" className="text-[#FF7A00] hover:underline">
              Mediterranean ADUs Milpitas
            </Link>
            <Link to="/contemporary-adu-design-milpitas" className="text-[#FF7A00] hover:underline">
              Contemporary ADUs Milpitas
            </Link>
            <Link to="/craftsman-adu-design-milpitas" className="text-[#FF7A00] hover:underline">
              Craftsman ADUs Milpitas
            </Link>
            <Link to="/spanish-colonial-adu-design-milpitas" className="text-[#FF7A00] hover:underline">
              Spanish Colonial ADUs Milpitas
            </Link>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default VictorianADUDesignMilpitas;